import { useState } from 'react';
import { api } from '../../../api';
import type { FaqGeneration, FaqQaPair } from '../../../types';

interface Props {
  generation: FaqGeneration;
  onUpdate: (updated: FaqGeneration) => void;
}

export function FaqGenerationCard({ generation, onUpdate }: Props) {
  const [pairs, setPairs] = useState<FaqQaPair[]>(generation.qaPairs);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [toast, setToast] = useState('');

  const isDraft = generation.status === 'draft';
  const isApproved = generation.status === 'approved';

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const updated = await api.faqGenerations.updateQaPairs(generation.id, pairs);
      onUpdate(updated);
      setPairs(updated.qaPairs);
      showToast('Changes saved');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    setApproving(true);
    try {
      const approved = await api.faqGenerations.approve(generation.id);
      const { jsonLd } = await api.faqGenerations.getJsonLd(generation.id);
      await navigator.clipboard.writeText(jsonLd);
      onUpdate(approved);
      showToast('Approved! JSON-LD copied to clipboard');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Approve failed');
    } finally {
      setApproving(false);
    }
  }

  async function handleDiscard() {
    setDiscarding(true);
    try {
      const discarded = await api.faqGenerations.discard(generation.id);
      onUpdate(discarded);
      setConfirmDiscard(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Discard failed');
    } finally {
      setDiscarding(false);
    }
  }

  async function handleCopyJsonLd() {
    try {
      const { jsonLd } = await api.faqGenerations.getJsonLd(generation.id);
      await navigator.clipboard.writeText(jsonLd);
      showToast('JSON-LD copied to clipboard');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Copy failed');
    }
  }

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-gray-500 truncate font-medium">{generation.sourceUrl}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {new Date(generation.createdAt).toLocaleDateString()} · {generation.model}
          </p>
        </div>
        {generation.status !== 'draft' && (
          <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
            isApproved ? 'bg-green-50 text-brand-green' : 'bg-gray-100 text-gray-400'
          }`}>
            {isApproved ? 'Approved' : 'Discarded'}
          </span>
        )}
      </div>

      {/* Q&A pairs */}
      <div className="space-y-3">
        {pairs.map((pair, i) => (
          <div key={pair.id} className="border border-gray-100 rounded-lg p-3 space-y-2">
            <div className="flex items-start gap-2">
              {isDraft ? (
                <textarea
                  value={pair.question}
                  onChange={(e) => {
                    const next = pairs.map((p, idx) =>
                      idx === i ? { ...p, question: e.target.value } : p,
                    );
                    setPairs(next);
                  }}
                  rows={2}
                  aria-label="Question"
                  className="flex-1 text-sm font-medium border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-green resize-none"
                />
              ) : (
                <p className="flex-1 text-sm font-medium text-brand-ink">{pair.question}</p>
              )}
              {pair.edited && (
                <span className="shrink-0 text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full mt-1">
                  Edited
                </span>
              )}
            </div>
            {isDraft ? (
              <textarea
                value={pair.answer}
                onChange={(e) => {
                  const next = pairs.map((p, idx) =>
                    idx === i ? { ...p, answer: e.target.value } : p,
                  );
                  setPairs(next);
                }}
                rows={3}
                aria-label="Answer"
                className="w-full text-sm text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-green resize-none"
              />
            ) : (
              <p className="text-sm text-gray-600 leading-relaxed">{pair.answer}</p>
            )}
          </div>
        ))}
      </div>

      {/* Draft actions */}
      {isDraft && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-50">
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className="text-xs bg-gray-100 text-brand-ink px-3 py-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button
            onClick={() => void handleApprove()}
            disabled={approving}
            className="text-xs bg-brand-green text-white px-3 py-1.5 rounded-lg hover:bg-brand-green-dark disabled:opacity-50 transition-colors"
          >
            {approving ? 'Approving…' : 'Approve & copy JSON-LD'}
          </button>
          <button
            onClick={() => setConfirmDiscard(true)}
            className="text-xs text-gray-400 hover:text-red-500 px-3 py-1.5 rounded-lg transition-colors ml-auto"
          >
            Discard
          </button>
        </div>
      )}

      {/* Approved actions */}
      {isApproved && (
        <div className="space-y-3 pt-2 border-t border-gray-50">
          <button
            onClick={() => void handleCopyJsonLd()}
            className="text-xs bg-brand-green text-white px-3 py-1.5 rounded-lg hover:bg-brand-green-dark transition-colors"
          >
            Copy JSON-LD
          </button>
          <details className="text-xs text-gray-400">
            <summary className="cursor-pointer hover:text-gray-600 list-none">
              Where do I paste this?
            </summary>
            <p className="mt-2 text-gray-500 leading-relaxed">
              Paste the JSON-LD script block into the <code>&lt;head&gt;</code> of the specific page
              it relates to. Use one FAQPage block per page — multiple blocks on the same URL are
              ignored by search engines and AI crawlers. If you&apos;re on WordPress, a plugin like
              Rank Math or Yoast lets you add custom schema without editing templates.
            </p>
          </details>
        </div>
      )}

      {/* Confirm discard dialog */}
      {confirmDiscard && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="font-semibold text-brand-ink mb-2">Discard this draft?</h3>
            <p className="text-sm text-gray-500 mb-4">
              This will permanently mark the draft as discarded. You can generate a new one at any time.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDiscard(false)}
                className="text-sm text-gray-500 px-4 py-2 rounded-lg hover:text-brand-ink"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleDiscard()}
                disabled={discarding}
                className="text-sm bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {discarding ? 'Discarding…' : 'Discard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <p className="text-xs text-brand-green">{toast}</p>}
    </div>
  );
}
